import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon } from '@blueprintjs/core';
import { Hero, FeatureCard, TestimonialCard } from '../../components';
import type { Feature, Testimonial } from '../../types';
import './HomePage.scss';

const features: Feature[] = [
  {
    id: '1',
    icon: 'heart',
    title: 'Critical Illness Protection',
    description: 'Access your benefits when diagnosed with cancer, heart attack, stroke, or other critical illnesses. Get financial support when you need it most.',
  },
  {
    id: '2',
    icon: 'shield',
    title: 'Chronic Illness Coverage',
    description: 'If you become unable to perform daily activities or experience cognitive impairment, your Living Benefits kick in to support you.',
  },
  {
    id: '3',
    icon: 'pulse',
    title: 'Critical Injury Benefits',
    description: 'Protection for paralysis, coma, major burns, and severe brain injuries. Your policy works for you when accidents happen.',
  },
  {
    id: '4',
    icon: 'home',
    title: 'Mortgage Protection',
    description: 'Keep your family in their home. Living Benefits can cover mortgage payments when illness or injury strikes.',
  },
  {
    id: '5',
    icon: 'bank-account',
    title: 'Tax-Free Benefits',
    description: 'Access your death benefit tax-free while you\'re still alive. Use the funds for medical bills, lost income, or any purpose.',
  },
  {
    id: '6',
    icon: 'endorsed',
    title: 'No Extra Premium',
    description: 'Living Benefits riders are included at no additional cost. Same price as traditional life insurance with more protection.',
  },
];

const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Sarah M.',
    quote: 'When I was diagnosed with breast cancer, I thought my family would lose everything. My Living Benefits policy gave us $180,000 tax-free to cover my treatment and replace my income. I\'m now cancer-free and my family is secure.',
    condition: 'Breast Cancer Survivor',
  },
  {
    id: '2',
    name: 'Michael T.',
    quote: 'After my heart attack, I couldn\'t work for 8 months. Traditional insurance would have left us struggling. Living Benefits covered our mortgage and bills while I recovered. It truly saved our home.',
    condition: 'Heart Attack Recovery',
  },
  {
    id: '3',
    name: 'Jennifer L.',
    quote: 'I never thought I\'d need my life insurance while alive. When my husband had a stroke, our Living Benefits policy provided $250,000 for his care. It\'s not just life insurance—it\'s living insurance.',
    condition: 'Spouse\'s Stroke',
  },
];

const HomePage: React.FC = () => {
  return (
    <div className="home-page">
      <Hero
        title="Life Insurance That Works While You're Still Alive"
        subtitle="Discover Living Benefits — modern life insurance that provides tax-free access to your death benefit when you're diagnosed with a critical, chronic, or terminal illness. Protection for your family, even if you survive."
        ctaText="Get Your Free Quote"
        ctaLink="/contact"
      />

      {/* What Are Living Benefits Section */}
      <section className="section section--light">
        <div className="container">
          <div className="home-page__intro">
            <span className="home-page__eyebrow">The New Kind of Life Insurance</span>
            <h2>What Are Living Benefits?</h2>
            <p className="home-page__intro-text">
              Traditional life insurance only pays when you die. But what if you get seriously ill and survive?
              Living Benefits life insurance includes Accelerated Benefit Riders (ABRs) that let you access
              your death benefit while you're still alive — when you need the money most.
            </p>
            <div className="home-page__comparison">
              <div className="home-page__comparison-item home-page__comparison-item--old">
                <h4><Icon icon="cross" /> Traditional Life Insurance</h4>
                <ul>
                  <li>Only pays out when you die</li>
                  <li>No help during illness</li>
                  <li>Family struggles with medical bills</li>
                  <li>Outdated protection model</li>
                </ul>
              </div>
              <div className="home-page__comparison-item home-page__comparison-item--new">
                <h4><Icon icon="tick" /> Living Benefits Insurance</h4>
                <ul>
                  <li>Access funds while alive</li>
                  <li>Protection during critical illness</li>
                  <li>Cover medical costs tax-free</li>
                  <li>Same price, more protection</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-5">
            <span className="home-page__eyebrow">Comprehensive Protection</span>
            <h2>How Living Benefits Protect You</h2>
            <p className="home-page__section-subtitle">
              Get access to your death benefit for any of these qualifying conditions —
              at no extra cost compared to traditional life insurance.
            </p>
          </div>
          <div className="home-page__features-grid">
            {features.map((feature) => (
              <FeatureCard key={feature.id} feature={feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="section section--gradient">
        <div className="container">
          <div className="home-page__stats-section">
            <h2>The Reality of Medical Financial Crisis</h2>
            <p>These statistics show why Living Benefits are essential for every family.</p>
            <div className="home-page__stats-grid">
              <div className="home-page__stat-item">
                <span className="home-page__stat-number">66%</span>
                <span className="home-page__stat-label">of bankruptcies are tied to medical issues</span>
              </div>
              <div className="home-page__stat-item">
                <span className="home-page__stat-number">1 in 3</span>
                <span className="home-page__stat-label">Americans will be diagnosed with cancer</span>
              </div>
              <div className="home-page__stat-item">
                <span className="home-page__stat-number">70%</span>
                <span className="home-page__stat-label">of Americans are under or uninsured</span>
              </div>
              <div className="home-page__stat-item">
                <span className="home-page__stat-number">$0</span>
                <span className="home-page__stat-label">extra cost for Living Benefits riders</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section section--light">
        <div className="container">
          <div className="text-center mb-5">
            <span className="home-page__eyebrow">Real Stories</span>
            <h2>Lives Changed by Living Benefits</h2>
            <p className="home-page__section-subtitle">
              Hear from real people whose Living Benefits policies provided financial
              security during life's most challenging moments.
            </p>
          </div>
          <div className="home-page__testimonials-grid">
            {testimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section--dark">
        <div className="container">
          <div className="home-page__cta">
            <h2>Ready to Protect Your Family?</h2>
            <p>
              Get a free, no-obligation quote and discover how Living Benefits
              can provide the protection your family deserves.
            </p>
            <div className="home-page__cta-buttons">
              <Link to="/contact">
                <Button
                  intent="success"
                  large
                  rightIcon={<Icon icon="arrow-right" />}
                >
                  Get Your Free Quote
                </Button>
              </Link>
              <Link to="/living-benefits">
                <Button large outlined className="home-page__cta-secondary">
                  Learn More About Living Benefits
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
